import {
  onLoginMenuShown,
  presentLoggedInWelcome,
  resolveAuthIfLoggedIn,
  validateExistingSession,
} from './auth-gate';
import { authBypassed } from '../config/env';
import { hasSession } from '../net/session';
import { isTelegramMiniApp } from './telegram-auth';

const IMG = '/Splash/images';
const AUD = '/Splash/audio';

const CONFIG = {
  TIMING: {
    CRACK1: 870,
    CRACK2: 1230,
    SWITCH_GEODE: 1950,
    PTERO1_FRAME_30: 900,
    PTERO1_END: 1140,
    BOOGA_OOGA: 660,
    PTERO_SHRIEK_DELAY: 1200,
    GEODE_TITLE_SWAP: 1500,
    GEODE_SMASH_SFX: 2040,
    LOGIN_FADE_IN: 3500,
  },
  SHAKE: {
    DURATION: 3150,
    EASE_IN: 800,
    EASE_OUT: 1800,
    MAX_INTENSITY: 7,
    CRACK1_STRENGTH: 18,
    CRACK2_STRENGTH: 14,
  },
  LAVA: {
    PARTICLE_COUNT: 40,
    WIDTH: 1400,
    HEIGHT: 2400,
  },
  PARALLAX: {
    MAX_OFFSET: 28,
    LERP_SPEED: 0.06,
  },
};

type SplashEls = {
  layers: NodeListOf<HTMLElement>;
  crack1: HTMLElement;
  crack2: HTMLElement;
  gifEl: HTMLImageElement;
  staticEl: HTMLImageElement;
  ptero1: HTMLElement;
  ptero2: HTMLElement;
  ptero3: HTMLImageElement;
  startScreen: HTMLElement | null;
  continueScreen: HTMLElement | null;
  runningBooga: HTMLImageElement;
  scene: HTMLElement;
  lavaCanvas: HTMLCanvasElement;
  finalLoginMenu: HTMLElement | null;
};

type SplashAudio = {
  sfx1: HTMLAudioElement;
  sfx2: HTMLAudioElement;
  boogaSfx: HTMLAudioElement;
  quakeSfx: HTMLAudioElement;
  smashSfx: HTMLAudioElement;
  pteroShriekSfx: HTMLAudioElement;
  stoneSfx: HTMLAudioElement;
  music: HTMLAudioElement;
};

const State = {
  gifStart: null as number | null,
  boogaStartTime: null as number | null,
  pteroStartTime: null as number | null,
  done: false,
  sfx1played: false,
  sfx2played: false,
  shake1done: false,
  shake2done: false,
  shakeAmt: 0,
  eqX: 0,
  eqY: 0,
  ptero3Blob: null as Blob | null,
  tx: 0,
  ty: 0,
  cx: 0,
  cy: 0,
};

let els: SplashEls | null = null;
let AudioAssets: SplashAudio | null = null;
let loginMenuResolve: (() => void) | null = null;
let loginMenuPromise: Promise<void> | null = null;
let initialized = false;
let geodeSrcURI = '';
let assetsReady = false;
let phase2Started = false;
let lavaStarted = false;
let tickStarted = false;
let tapInFlight = false;

function splashAudio(name: string) {
  const audio = new Audio(`${AUD}/${name}.mp3`);
  audio.preload = 'none';
  return audio;
}

function hydrateSplashDeferredImages({
  loginMenuOnly = false,
  sceneOnly = false,
}: { loginMenuOnly?: boolean; sceneOnly?: boolean } = {}) {
  const root = document.getElementById('auth-shell');
  if (!root) return;
  root.querySelectorAll('img[data-src]').forEach((img) => {
    const el = img as HTMLImageElement;
    if (el.dataset.noHydrate === 'true') return;
    if (loginMenuOnly && !el.closest('#finalLoginMenu')) return;
    if (sceneOnly && el.closest('#finalLoginMenu')) return;
    if (!el.getAttribute('src')) {
      el.src = el.dataset.src ?? '';
    }
  });
}

function runPhase2() {
  if (phase2Started || !els || !AudioAssets) return;
  phase2Started = true;

  State.pteroStartTime = null;
  els.ptero1.style.display = 'none';

  if (State.ptero3Blob) els.ptero3.src = URL.createObjectURL(State.ptero3Blob);
  els.ptero3.style.display = 'block';

  setTimeout(() => {
    els?.ptero2 && (els.ptero2.style.display = 'none');
  }, 90);

  els.runningBooga.style.display = 'block';
  if (!els.runningBooga.getAttribute('src')) {
    els.runningBooga.src = els.runningBooga.dataset.src ?? '';
  }
  els.runningBooga.classList.add('booga-run-anim');

  State.boogaStartTime = performance.now();
  AudioAssets.quakeSfx.currentTime = 0;
  void AudioAssets.quakeSfx.play().catch(() => {});

  setTimeout(() => {
    if (!AudioAssets) return;
    AudioAssets.boogaSfx.currentTime = 0;
    void AudioAssets.boogaSfx.play().catch(() => {});
  }, CONFIG.TIMING.BOOGA_OOGA);

  setTimeout(() => {
    if (!AudioAssets) return;
    AudioAssets.pteroShriekSfx.currentTime = 0;
    void AudioAssets.pteroShriekSfx.play().catch(() => {});
  }, CONFIG.TIMING.PTERO_SHRIEK_DELAY);

  setTimeout(() => {
    if (els) els.staticEl.src = geodeSrcURI;
  }, CONFIG.TIMING.GEODE_TITLE_SWAP);

  setTimeout(() => {
    if (!AudioAssets) return;
    AudioAssets.smashSfx.currentTime = 0;
    void AudioAssets.smashSfx.play().catch(() => {});
  }, CONFIG.TIMING.GEODE_SMASH_SFX);

  setTimeout(showLoginMenuUI, CONFIG.TIMING.LOGIN_FADE_IN);

  els.continueScreen?.remove();
}

function tryStartPhase2() {
  if (!assetsReady || !State.done || phase2Started) return;
  runPhase2();
}

export function notifyAssetsLoadComplete() {
  assetsReady = true;
  tryStartPhase2();
}

function unlockAudio(audio: HTMLAudioElement) {
  audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
    })
    .catch(() => {});
}

function triggerShake(strength: number) {
  State.shakeAmt = strength;
}

function boogaEarthquake(e: number) {
  if (e >= CONFIG.SHAKE.DURATION) return 0;
  if (e < CONFIG.SHAKE.EASE_IN) {
    return (Math.sin((e / CONFIG.SHAKE.EASE_IN) * Math.PI - Math.PI / 2) + 1) / 2;
  }
  if (e > CONFIG.SHAKE.DURATION - CONFIG.SHAKE.EASE_OUT) {
    return (
      (Math.sin(((CONFIG.SHAKE.DURATION - e) / CONFIG.SHAKE.EASE_OUT) * Math.PI - Math.PI / 2) + 1) /
      2
    );
  }
  return 1;
}

function tick(now: number) {
  if (!els) return;

  State.cx += (State.tx - State.cx) * CONFIG.PARALLAX.LERP_SPEED;
  State.cy += (State.ty - State.cy) * CONFIG.PARALLAX.LERP_SPEED;

  State.eqX = 0;
  State.eqY = 0;
  if (State.boogaStartTime !== null) {
    const t = now - State.boogaStartTime;
    if (t < CONFIG.SHAKE.DURATION) {
      const intens = boogaEarthquake(t);
      State.eqX = (Math.random() - 0.5) * 2 * CONFIG.SHAKE.MAX_INTENSITY * intens;
      State.eqY = (Math.random() - 0.5) * 2 * CONFIG.SHAKE.MAX_INTENSITY * intens;
    } else {
      State.boogaStartTime = null;
    }
  }

  State.shakeAmt *= 0.8;
  const sx = (State.shakeAmt > 0.3 ? (Math.random() - 0.5) * 2 * State.shakeAmt : 0) + State.eqX;
  const sy = (State.shakeAmt > 0.3 ? (Math.random() - 0.5) * 2 * State.shakeAmt : 0) + State.eqY;
  els.scene.style.transform = `translateX(-50%) translate(${sx}px,${sy}px)`;

  els.layers.forEach((l) => {
    const d = parseFloat(l.dataset.depth ?? '') || 0.5;
    l.style.transform = `translate3d(${State.cx * CONFIG.PARALLAX.MAX_OFFSET * d}px,${State.cy * CONFIG.PARALLAX.MAX_OFFSET * d}px,0)`;
  });

  if (State.gifStart !== null && !State.done && AudioAssets) {
    const e = now - State.gifStart;

    if (e >= CONFIG.TIMING.CRACK1 && !State.sfx1played) {
      State.sfx1played = true;
      void AudioAssets.sfx1.play();
    }
    if (e >= CONFIG.TIMING.CRACK1 && !State.shake1done) {
      State.shake1done = true;
      triggerShake(CONFIG.SHAKE.CRACK1_STRENGTH);
      els.crack1.style.opacity = '1';
    }

    if (e >= CONFIG.TIMING.CRACK2 && !State.sfx2played) {
      State.sfx2played = true;
      void AudioAssets.sfx2.play();
    }
    if (e >= CONFIG.TIMING.CRACK2 && !State.shake2done) {
      State.shake2done = true;
      triggerShake(CONFIG.SHAKE.CRACK2_STRENGTH);
      els.crack2.style.opacity = '1';
    }

    if (e >= CONFIG.TIMING.SWITCH_GEODE) {
      State.done = true;
      els.staticEl.style.opacity = '1';
      els.gifEl.src = '';
      els.gifEl.dataset.noHydrate = 'true';
      els.ptero1.style.display = 'block';
      State.pteroStartTime = now;

      setTimeout(() => {
        if (!AudioAssets) return;
        AudioAssets.stoneSfx.currentTime = 0;
        void AudioAssets.stoneSfx.play().catch(() => {});
      }, CONFIG.TIMING.PTERO1_FRAME_30);

      tryStartPhase2();
    }
  }

  if (State.pteroStartTime !== null && now - State.pteroStartTime >= CONFIG.TIMING.PTERO1_END) {
    els.ptero1.style.display = 'none';
    els.ptero2.style.display = 'block';
    State.pteroStartTime = null;
  }

  requestAnimationFrame(tick);
}

function resolveLoginMenu() {
  if (loginMenuResolve) {
    loginMenuResolve();
    loginMenuResolve = null;
  }
}

function showLoginMenuUI() {
  if (authBypassed()) {
    resolveLoginMenu();
    return;
  }

  hydrateSplashDeferredImages({ loginMenuOnly: true });
  // Keep shell visible; only reveal the login overlay over the cinematic scene.
  const shell = document.getElementById('auth-shell');
  shell?.classList.remove('auth-shell--hidden');
  shell?.classList.add('auth-shell--visible');
  els?.finalLoginMenu?.classList.remove('hidden');
  onLoginMenuShown();
  resolveLoginMenu();
}

export function waitForSplashLoginMenu() {
  if (!loginMenuPromise) {
    loginMenuPromise = new Promise<void>((resolve) => {
      loginMenuResolve = resolve;
    });
  }
  return loginMenuPromise;
}

/** Skip the cinematic/login-menu wait when Telegram auth already succeeded. */
export function skipSplashLoginGate() {
  waitForSplashLoginMenu();
  resolveLoginMenu();
}

export function stopSplashMusic() {
  if (!AudioAssets?.music) return;
  const music = AudioAssets.music;
  const fade = setInterval(() => {
    if (music.volume > 0.05) {
      music.volume = Math.max(0, music.volume - 0.05);
    } else {
      music.pause();
      music.currentTime = 0;
      music.volume = 0.6;
      clearInterval(fade);
    }
  }, 50);
}

export function hideSplashBoot() {
  const splashBoot = document.getElementById('splash-boot');
  splashBoot?.classList.add('splash-boot--hidden');
  stopSplashMusic();
}

export function showSplashBoot() {
  const splashBoot = document.getElementById('splash-boot');
  splashBoot?.classList.remove('splash-boot--hidden');
}

function dismissStartScreen() {
  const startScreen = els?.startScreen;
  startScreen?.classList.add('hidden');
  if (startScreen) setTimeout(() => startScreen.remove(), 500);
}

function skipCinematicWithWelcome() {
  dismissStartScreen();
  document.getElementById('splash-boot')?.classList.add('splash-boot--hidden');
  presentLoggedInWelcome();
  resolveAuthIfLoggedIn();
  resolveLoginMenu();
}

function runCinematicSequence() {
  if (!els || !AudioAssets) return;

  els.scene.classList.add('scene--cinematic');
  hydrateSplashDeferredImages({ sceneOnly: true });
  Object.values(AudioAssets).forEach((audio) => {
    if (audio !== AudioAssets!.music) unlockAudio(audio);
  });
  void AudioAssets.music.play().catch(() => {});

  els.gifEl.style.display = 'block';

  const cacheSrc = els.gifEl.dataset.src ?? '';
  els.gifEl.src = '';
  els.gifEl.src = cacheSrc;

  State.gifStart = performance.now();
  dismissStartScreen();
}

async function onTapToStart() {
  if (tapInFlight) return;
  if (!els?.startScreen || els.startScreen.classList.contains('hidden')) return;
  tapInFlight = true;

  // Returning players: validate session, then skip cinematic → welcome → game.
  if (!authBypassed() && hasSession()) {
    const startBtn = document.getElementById('startBtn');
    if (startBtn) startBtn.textContent = 'CHECKING…';
    els.startScreen.style.pointerEvents = 'none';

    const valid = await validateExistingSession();
    if (valid) {
      skipCinematicWithWelcome();
      return;
    }

    if (startBtn) startBtn.textContent = 'TAP TO START';
    els.startScreen.style.pointerEvents = '';
  }

  runCinematicSequence();
}

export function runSplashSequence() {
  if (!initialized) initSplashScreen();
  void onTapToStart();
}

export function resetSplashForRelogin() {
  hideSplashBoot();
  document.getElementById('splash-boot')?.classList.add('splash-boot--hidden');
  const menu = document.getElementById('finalLoginMenu');
  menu?.classList.remove('hidden');
  hydrateSplashDeferredImages({ loginMenuOnly: true });
  stopSplashMusic();
  loginMenuPromise = null;
  loginMenuResolve = null;
  waitForSplashLoginMenu();
  resolveLoginMenu();
}

export function initSplashScreen() {
  if (initialized) return;
  initialized = true;

  const crack1 = document.getElementById('crack1');
  const crack2 = document.getElementById('crack2');
  const gifEl = document.getElementById('gif') as HTMLImageElement | null;
  const staticEl = document.getElementById('static') as HTMLImageElement | null;
  const ptero1 = document.getElementById('ptero1');
  const ptero2 = document.getElementById('ptero2');
  const ptero3 = document.getElementById('ptero3') as HTMLImageElement | null;
  const runningBooga = document.getElementById('runningBooga') as HTMLImageElement | null;
  const scene = document.getElementById('scene');
  const lavaCanvas = document.getElementById('lavaCanvas') as HTMLCanvasElement | null;

  if (!scene || !lavaCanvas || !crack1 || !crack2 || !gifEl || !staticEl || !ptero1 || !ptero2 || !ptero3 || !runningBooga) {
    return;
  }

  els = {
    layers: document.querySelectorAll('#auth-shell .parallax-layer'),
    crack1,
    crack2,
    gifEl,
    staticEl,
    ptero1,
    ptero2,
    ptero3,
    startScreen: document.getElementById('startScreen'),
    continueScreen: document.getElementById('continueScreen'),
    runningBooga,
    scene,
    lavaCanvas,
    finalLoginMenu: document.getElementById('finalLoginMenu'),
  };

  AudioAssets = {
    sfx1: splashAudio('drop_1'),
    sfx2: splashAudio('drop_2'),
    boogaSfx: splashAudio('ooga_booga'),
    quakeSfx: new Audio(`${AUD}/quake.mp3`),
    smashSfx: new Audio(`${AUD}/smash.mp3`),
    pteroShriekSfx: splashAudio('ptero_shriek'),
    stoneSfx: splashAudio('small_stone'),
    music: new Audio(`${AUD}/savannah.mp3`),
  };

  AudioAssets.music.preload = 'auto';
  AudioAssets.music.loop = true;
  AudioAssets.music.volume = 0.6;
  AudioAssets.quakeSfx.preload = 'none';
  AudioAssets.smashSfx.preload = 'none';

  geodeSrcURI = `${IMG}/title_drop_geode.gif`;

  const pteroPre1 = new Image();
  pteroPre1.src = `${IMG}/pterodactyl1.gif`;
  const pteroPre2 = new Image();
  pteroPre2.src = `${IMG}/pterodactyl2.gif`;
  const preloadGeode = new Image();
  preloadGeode.src = geodeSrcURI;
  const preloadStatic = new Image();
  preloadStatic.src = `${IMG}/static_title.png`;

  void fetch(`${IMG}/pterodactyl3.gif`)
    .then((r) => r.blob())
    .then((blob) => {
      State.ptero3Blob = blob;
    });

  const lctx = els.lavaCanvas.getContext('2d');
  if (!lctx) return;

  els.lavaCanvas.width = CONFIG.LAVA.WIDTH;
  els.lavaCanvas.height = CONFIG.LAVA.HEIGHT;

  const LAVA_COLORS = [
    'rgba(255,80,0,',
    'rgba(255,140,0,',
    'rgba(255,200,30,',
    'rgba(255,60,0,',
    'rgba(255,255,80,',
  ];

  class Particle {
    x = 0;
    y = 0;
    vy = 0;
    vx = 0;
    r = 0;
    life = 0;
    maxLife = 0;
    color = LAVA_COLORS[0];
    wobble = 0;

    constructor() {
      this.reset();
    }
    reset() {
      this.x = Math.random() * CONFIG.LAVA.WIDTH;
      this.y = (0.92 + Math.random() * 0.08) * CONFIG.LAVA.HEIGHT;
      this.vy = -(0.6 + Math.random() * 2.2);
      this.vx = (Math.random() - 0.5) * 0.8;
      this.r = 4 + Math.random() * 14;
      this.life = 0;
      this.maxLife = 60 + Math.random() * 100;
      this.color = LAVA_COLORS[Math.floor(Math.random() * LAVA_COLORS.length)]!;
      this.wobble = Math.random() * Math.PI * 2;
    }
    update() {
      this.life++;
      this.wobble += 0.04;
      this.x += this.vx + Math.sin(this.wobble) * 0.4;
      this.y += this.vy;
      this.vy *= 0.995;
      if (this.life >= this.maxLife) this.reset();
    }
    draw(ctx: CanvasRenderingContext2D) {
      const progress = this.life / this.maxLife;
      let alpha =
        progress < 0.1 ? progress / 0.1 : progress < 0.7 ? 1 : 1 - (progress - 0.7) / 0.3;
      alpha *= 0.85;

      const grd = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.r);
      grd.addColorStop(0, this.color + alpha + ')');
      grd.addColorStop(0.5, this.color + alpha * 0.6 + ')');
      grd.addColorStop(1, this.color + '0)');

      ctx.beginPath();
      ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();
    }
  }

  const particles = Array.from({ length: CONFIG.LAVA.PARTICLE_COUNT }, () => {
    const p = new Particle();
    p.life = Math.floor(Math.random() * p.maxLife);
    return p;
  });

  function animateLava() {
    if (!lctx) return;
    lctx.clearRect(0, 0, CONFIG.LAVA.WIDTH, CONFIG.LAVA.HEIGHT);
    for (const p of particles) {
      p.update();
      p.draw(lctx);
    }
    requestAnimationFrame(animateLava);
  }
  if (!lavaStarted) {
    lavaStarted = true;
    animateLava();
  }

  window.addEventListener('mousemove', (e) => {
    State.tx = (e.clientX / innerWidth - 0.5) * 2;
    State.ty = (e.clientY / innerHeight - 0.5) * 2;
  });

  const DeviceOrientationEventCtor = window.DeviceOrientationEvent as
    | (typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<string>;
      })
    | undefined;

  if (typeof DeviceOrientationEventCtor?.requestPermission === 'function') {
    document.addEventListener(
      'click',
      () => {
        void DeviceOrientationEventCtor.requestPermission?.();
      },
      { once: true }
    );
  }
  window.addEventListener(
    'deviceorientation',
    (e) => {
      if (e.gamma == null) return;
      State.tx = Math.max(-35, Math.min(35, e.gamma)) / 35;
      State.ty = (Math.max(15, Math.min(65, e.beta ?? 45)) - 40) / 25;
    },
    { passive: true }
  );

  if (els.startScreen) {
    els.startScreen.addEventListener('click', () => void onTapToStart());
  }

  // Dark slate first; reveal TAP TO START once splash boot + brand font are ready.
  void revealTapToStart();

  if (isTelegramMiniApp()) {
    if (hasSession()) {
      // Telegram already signed in — skip TAP TO START + cinematic.
      void validateExistingSession().then((valid) => {
        if (valid) skipCinematicWithWelcome();
        else void onTapToStart();
      });
    } else {
      void onTapToStart();
    }
  }

  if (!tickStarted) {
    tickStarted = true;
    requestAnimationFrame(tick);
  }
}

async function revealTapToStart() {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* ignore */
  }
  // One paint of pure slate before the CTA fades in.
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  const btn = document.getElementById('startBtn');
  if (!btn || btn.closest('#startScreen')?.classList.contains('hidden')) return;
  btn.classList.remove('start-btn--pending');
  btn.classList.add('start-btn--ready');
}
